import { disposeMesh } from 'src/utils/object3DHelpers';
import { ComponentInteractionType, Inputs, SceneComponent } from './types';
import { calculateAngleOX, calculateMidpoint } from 'src/utils/three';
import { LoadingState } from 'src/sdk-components/VSObject';
import { Outputs } from 'src/sdk-components/HTMLComponent/types';
import {
  createRoundedRectangleMesh,
  createTriangleMesh,
  roundRect,
} from 'src/sdk-components/TeleportComponent/utils';
import { TeleportGroupType } from 'src/service/navigation/types';

const DEFAULT_INPUTS = {
  colorDefault: 'white',
  opacityDefault: 0.5,
  opacityHover: 1,
  adminPanelAccess: false,
  rtl: false,
  type: TeleportGroupType.Elevator,
  isTpUp: false,
  navClickOnEnter: 'Click to enter',
  navUseTheStairs: 'Use the Stairs'
};

export class TeleportComponent extends SceneComponent {
  private root: any = null;

  private pivot: any = null;

  private circle: any = null;

  private currentOpacity = 0.2;

  private clicked = false;

  private connectionLine: any = null;

  public plane: any;

  public colliderPlane: any;

  private label: any;

  private upperChevron: any;

  private lowerChevron: any;

  inputs: Inputs = {
    ...DEFAULT_INPUTS,
  };

  outputs = {
    loadingState: LoadingState.Idle,
  } as Outputs;

  events = {
    [ComponentInteractionType.CLICK]: true,
    [ComponentInteractionType.HOVER]: true,
    [ComponentInteractionType.DRAG]: false,
  };

  declare context: any;

  public rendererElement: any;

  private startTime = Date.now();

  onInit() {
    const THREE = this.context.three;

    this.root = new THREE.Object3D();
    this.pivot = new THREE.Object3D();
    // setting the collider to add the click event
    this.outputs.collider = this.root;
    this.outputs.objectRoot = this.root;
    this.root.add(this.pivot);
    this.currentOpacity = this.inputs.opacityDefault;

    if (this.inputs.adminPanelAccess) {
      this.makeCircle();
      this.outputs.loadingState = LoadingState.Loaded;
    }

    if (!this.inputs.adminPanelAccess) {
      this.makePlane();
      this.outputs.loadingState = LoadingState.Loaded;
    }
  }

  onDestroy() {
    if (this.circle) {
      disposeMesh(this.circle);
      this.circle = null;
    }
    if (this.connectionLine) {
      this.removeConnectionLine();
    }
  }

  // Called back when any inputs properties change like localPosition or localRotation
  onInputsUpdated() {
    this.onInit();
  }

  // used to fire it from outside event handler,
  // as internal handler doesn't work if outside handler is defined
  handleHover(event: any) {
    if (!this.clicked) {
      if (event.hover) {
        this.currentOpacity = this.inputs.opacityHover;
      } else {
        this.currentOpacity = this.inputs.opacityDefault;
      }
      if (this.circle) this.circle.material.opacity = this.currentOpacity;
    }
  }

  handleClick() {
    if (!this.clicked) {
      this.clicked = true;
    } else {
      this.clicked = false;
    }
    setTimeout(() => {
      this.clicked = false;
    }, 1000);
  }

  setVisible(visible: boolean) {
    if (this.circle) (this.circle.material as any).opacity = visible ? this.currentOpacity : 0;
  }

  // region Admin panel version
  private async makeCircle() {
    const THREE = this.context.three;

    if (this.circle) {
      disposeMesh(this.circle);
      this.circle = null;
    }

    const geometry = new THREE.RingGeometry(0.35, 0.5, 32);
    const material: any = new THREE.MeshBasicMaterial({
      color: this.inputs.colorDefault,
      transparent: true,
      opacity: this.currentOpacity,
      side: THREE.DoubleSide,
      blending: THREE.NormalBlending,
      depthWrite: true,
      depthTest: true,
    });

    this.circle = new THREE.Mesh(geometry, material);
    this.circle.position.y = 0.5;
    this.pivot.add(this.circle);
  }

  removeConnectionLine() {
    if (!this.connectionLine) return;
    this.root.parent.parent.remove(this.connectionLine);
    this.connectionLine = null;
  }

  drawConnectionLine(target: { x: number; y: number; z: number }) {
    if (!this.circle) return;
    this.removeConnectionLine();
    const THREE = this.context.three;

    const end = new THREE.Vector3(target.x, target.y, target.z);

    const start = new THREE.Vector3();
    this.circle.getWorldPosition(start);

    const distance = start.distanceTo(end);

    const subsegments = Math.abs(distance * 6);

    const positions = [start];

    const stepX = (end.x - start.x) / subsegments;
    const stepY = (end.y - start.y) / subsegments;
    const stepZ = (end.z - start.z) / subsegments;

    for (let i = 1; i < subsegments; i++) {
      const segmentPoint = new THREE.Vector3(
        start.x + stepX * i,
        start.y + stepY * i,
        start.z + stepZ * i,
      );
      positions.push(segmentPoint);
    }
    positions.push(end);

    const geometry = new THREE.PlaneGeometry(0.05, positions[0].distanceTo(positions[1]) / 2);
    const material = new THREE.MeshBasicMaterial({
      side: THREE.DoubleSide,
      color: 'white',
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
      depthTest: false,
    });
    const mesh = new THREE.Mesh(geometry, material);

    const angleLat = calculateAngleOX(start, end, THREE);

    const direction = new THREE.Vector3().subVectors(end, start);
    const upVector = new THREE.Vector3(0, 1, 0);
    const angleLon = direction.angleTo(upVector);

    const line = new THREE.Group();

    positions.forEach((position, index) => {
      if (!positions[index + 1]) return;

      const midPoint = calculateMidpoint(position, positions[index + 1], THREE);
      const sceneMesh = mesh.clone();
      const sceneMeshRoot = new THREE.Group();

      sceneMeshRoot.position.copy(midPoint);
      sceneMeshRoot.position.y = position.y; // Hardcoded for now

      sceneMeshRoot.rotation.x = -Math.PI / 2;
      sceneMeshRoot.rotation.z = angleLat;

      sceneMesh.rotation.x = Math.PI / 2 + angleLon;

      sceneMeshRoot.add(sceneMesh);
      line.add(sceneMeshRoot);
    });

    this.root.parent.parent.add(line);
    this.connectionLine = line;
  }
  // endregion

  // region Public version
  makePlane() {
    const THREE = this.context.three;
    const textureLoader = new THREE.TextureLoader();

    const containerMesh = createRoundedRectangleMesh({
      width: 245,
      height: 280,
      borderRadius: 50,
      color: '#FFFFFF',
      THREE,
    });
    this.pivot.add(containerMesh);

    const bottomBGMesh = createRoundedRectangleMesh({
      width: 245,
      height: 75,
      borderRadius: 36,
      color: '#F4F4F4',
      isHalfRounded: true,
      THREE,
    });
    bottomBGMesh.position.z = 1;
    bottomBGMesh.position.y = -280 / 2 + 75 / 2;
    this.pivot.add(bottomBGMesh);

    const teleportButton = createRoundedRectangleMesh({
      width: 220,
      height: 52,
      borderRadius: 27,
      color: '#2055FF',
      THREE,
    });
    teleportButton.position.z = 2;
    teleportButton.position.y = 52 / 2 - 280 / 2 + 12;
    this.pivot.add(teleportButton);

    this.outputs.collider = teleportButton;

    const ring = new THREE.Mesh(
      new THREE.RingGeometry(110 / 2, 142 / 2, 32),
      new THREE.MeshBasicMaterial({ color: 0xe4eaff, side: THREE.DoubleSide }),
    );
    this.pivot.add(ring);
    ring.position.z = 3;
    ring.position.y = 280 / 2 - 142 / 2 - 40;

    this.initLabel();
    this.label.scale.setScalar(500);
    this.label.position.z = 3;
    this.label.position.y = -280 / 2 + 40;

    textureLoader.load('/navigation/chevron-up.png', (texture) => {
      const geometry = new THREE.PlaneGeometry(texture.image.width / 2, texture.image.height / 2);
      const upperChevronMaterial = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        opacity: 0.5,
      });
      const lowerChevronMaterial = upperChevronMaterial.clone();
      const upperChevron = new THREE.Mesh(geometry, upperChevronMaterial);
      const lowerChevron = new THREE.Mesh(geometry, lowerChevronMaterial);
      upperChevron.position.z = 3;
      lowerChevron.position.z = 4;
      upperChevron.position.y = 280 / 2 - 142 / 2 - 40 + 14;
      lowerChevron.position.y = 280 / 2 - 142 / 2 - 40 - 13;
      // Rotate chevron if tp leads down
      if (!this.inputs.isTpUp) {
        upperChevron.rotation.z = Math.PI;
        lowerChevron.rotation.z = Math.PI;
      }
      this.upperChevron = upperChevron;
      this.lowerChevron = lowerChevron;
      this.pivot.add(upperChevron);
      this.pivot.add(lowerChevron);
    });

    const triangle = createTriangleMesh({ width: 23, height: 20, color: 'white', THREE });
    triangle.position.y = -280 / 2 - 5;
    triangle.position.z = 3;
    this.pivot.add(triangle);

    this.pivot.scale.setScalar(0.003);
    this.pivot.position.y = 0.5;
  }
  // endregion

  // region Label methods
  protected initLabel() {
    this.label = this.makeLabel(
      this.inputs?.type === TeleportGroupType.Elevator
          ? this.inputs.navClickOnEnter
          : this.inputs.navUseTheStairs,
      {
        fontsize: 48,
        textColor: {
          r: 255,
          g: 255,
          b: 255,
          a: 1.0,
        },
        borderColor: {
          r: 0,
          g: 0,
          b: 0,
          a: 0,
        },
        backgroundColor: {
          r: 0,
          g: 0,
          b: 0,
          a: 0,
        },
        borderThickness: 0,
      },
    );
    this.pivot.add(this.label);
  }

  protected makeLabel(message, parameters) {
    const THREE = this.context.three;
    if (parameters === undefined) parameters = {};

    const fontface = parameters.hasOwnProperty('fontface') ? parameters.fontface : 'Arial';
    const fontsize = parameters.hasOwnProperty('fontsize') ? parameters.fontsize : 36;
    const borderThickness = parameters.hasOwnProperty('borderThickness')
      ? parameters.borderThickness
      : 4;
    const borderColor = parameters.hasOwnProperty('borderColor')
      ? parameters.borderColor
      : {
          r: 0,
          g: 0,
          b: 0,
          a: 1.0,
        };
    const backgroundColor = parameters.hasOwnProperty('backgroundColor')
      ? parameters.backgroundColor
      : {
          r: 255,
          g: 255,
          b: 255,
          a: 1,
        };
    const textColor = parameters.hasOwnProperty('textColor')
      ? parameters.textColor
      : {
          r: 255,
          g: 255,
          b: 255,
          a: 1.0,
        };

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;
    context.font = `Bold ${fontsize}px ${fontface}`;
    const metrics = context.measureText(message);
    const textWidth = metrics.width;

    // Padding around text to avoid clipping/glyph overhang
    const paddingX = 12;
    const paddingY = Math.ceil(fontsize * 0.3);

    const dpr = (window && (window.devicePixelRatio || 1)) || 1;

    // Total logical (CSS pixel) size including border and padding
    const totalWidth = Math.ceil(textWidth + paddingX * 2 + borderThickness);
    const totalHeight = Math.ceil(fontsize + paddingY * 2 + borderThickness);

    // Set backing store size for crisp rendering and re-apply context state
    canvas.width = Math.max(1, Math.ceil(totalWidth * dpr));
    canvas.height = Math.max(1, Math.ceil(totalHeight * dpr));

    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.font = `Bold ${fontsize}px ${fontface}`;
    context.textAlign = 'left';
    context.textBaseline = 'top';

    // Background/border styles
    context.fillStyle = `rgba(${backgroundColor.r},${backgroundColor.g},${backgroundColor.b},${backgroundColor.a})`;
    context.strokeStyle = `rgba(${borderColor.r},${borderColor.g},${borderColor.b},${borderColor.a})`;

    context.lineWidth = borderThickness;
    // Draw rounded rectangle exactly matching the canvas logical area
    const rectX = borderThickness / 2;
    const rectY = borderThickness / 2;
    const rectW = totalWidth - borderThickness;
    const rectH = totalHeight - borderThickness;
    roundRect(context, rectX, rectY, rectW, rectH, 8);

    // Draw text within padding
    context.fillStyle = `rgba(${textColor.r}, ${textColor.g}, ${textColor.b}, 1.0)`;
    const textX = rectX + paddingX;
    const textY = rectY + paddingY;
    context.fillText(message, textX, textY);

    // Use the canvas directly as the label texture (no crop needed)
    const label = canvas;

    const texture = new THREE.Texture(label);
    texture.needsUpdate = true;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.encoding = THREE.sRGBEncoding;
    texture.repeat.set(1, 1);

    // Convert from backing store pixels to logical pixels before scaling
    const planeWidth = (label.width / dpr) / 1000; // scaling down
    const planeHeight = (label.height / dpr) / 1000;

    const plane = this.makeLabelPlane(planeWidth, planeHeight, texture);
    return plane;
  }

  private makeLabelPlane(width, height, texture) {
    const THREE = this.context.three;
    const geometry = new THREE.PlaneGeometry(width, height, 10, 10);

    const material = new THREE.MeshBasicMaterial({
      map: texture,
      side: THREE.DoubleSide,
      blending: THREE.NormalBlending,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    return new THREE.Mesh(geometry, material);
  }
  // endregion

  onTick(_tickDelta: number) {
    const elapsedTime = (Date.now() - this.startTime) / 1000;
    if (this.upperChevron) {
      this.upperChevron.material.opacity = 0.75 + 0.25 * Math.sin(elapsedTime * 2);
    }
    if (this.lowerChevron) {
    }
  }
}

export const teleportComponentType = 'treedis.teleportComponent';
export const makeTeleportComponent = () => new TeleportComponent();
